<script setup lang="ts">
import { Head, router } from "@inertiajs/vue3"
import { SbList, SbResultsBar } from "@unio/saltbox-ui"
import { refDebounced } from "@vueuse/core"
import { computed, ref, watch } from "vue"

import FilterBar from "@/components/FilterBar.vue"
import ListItemRow from "@/components/ListItemRow.vue"
import PaginationNav from "@/components/PaginationNav.vue"
import SearchInput from "@/components/SearchInput.vue"
import { useNavbar } from "@/composables/useNavbar"
import { ICON } from "@/lib/icons"
import { capturePath, itemsPath } from "@/routes"
import type { FilterChip, ItemSummary, PaginationMeta } from "@/types/models"

const { title } = useNavbar({ title: "Items" })

interface Props {
  items: ItemSummary[]
  query: string
  tag_filter: string | null
  type_filter: string | null
  container_filter: string | null
  attention_filter: boolean
  archive_scope: "active" | "archived"
  sort: string
  pagination: PaginationMeta
  available_tags: string[]
  available_types: { id: string; name: string }[]
  available_containers: { id: string; name: string; path: string | null }[]
}

const props = defineProps<Props>()

const search = ref(props.query)
const debouncedSearch = refDebounced(search, 300)

function buildParams(overrides: Record<string, string | null> = {}) {
  const params: Record<string, string> = {}
  const q = overrides.q !== undefined ? overrides.q : props.query
  const tag = overrides.tag !== undefined ? overrides.tag : props.tag_filter
  const typeId = overrides.type_id !== undefined ? overrides.type_id : props.type_filter
  const containerId =
    overrides.container_id !== undefined ? overrides.container_id : props.container_filter
  const attention =
    overrides.attention !== undefined ? overrides.attention : props.attention_filter ? "1" : null
  const sort = overrides.sort !== undefined ? overrides.sort : props.sort
  const archiveScope = overrides.scope !== undefined ? overrides.scope : props.archive_scope

  if (q) params.q = q
  if (tag) params.tag = tag
  if (typeId) params.type_id = typeId
  if (containerId) params.container_id = containerId
  if (attention) params.attention = attention
  if (sort && sort !== "recent") params.sort = sort
  if (archiveScope === "archived") params.scope = archiveScope

  return params
}

function navigate(overrides: Record<string, string | null> = {}) {
  router.get(itemsPath(), buildParams(overrides), { preserveState: true })
}

watch(debouncedSearch, (q) => {
  if (q === props.query) return
  navigate({ q: q || null })
})

const isFiltered = computed(
  () =>
    !!props.query ||
    !!props.tag_filter ||
    !!props.type_filter ||
    !!props.container_filter ||
    props.attention_filter,
)

const resultCountLabel = computed(() => {
  const count = props.pagination.total_count
  return `${count} ${count === 1 ? "item" : "items"}`
})

function clearFilters() {
  search.value = ""
  router.get(itemsPath(), props.archive_scope === "archived" ? { scope: "archived" } : {})
}

function selectArchiveScope(scope: "active" | "archived") {
  router.get(itemsPath(), scope === "archived" ? { scope } : {}, { preserveState: true })
}

const activeChips = computed<FilterChip[]>(() => {
  const chips: FilterChip[] = []
  if (props.query) {
    chips.push({
      label: `Search: ${props.query}`,
      color: "neutral",
      clear: () => {
        search.value = ""
        navigate({ q: null })
      },
    })
  }
  if (props.tag_filter) {
    chips.push({ label: props.tag_filter, color: "primary", clear: () => navigate({ tag: null }) })
  }
  const typeName = props.available_types.find((t) => t.id === props.type_filter)?.name
  if (typeName) {
    chips.push({ label: typeName, color: "primary", clear: () => navigate({ type_id: null }) })
  }
  const containerName = props.available_containers.find(
    (c) => c.id === props.container_filter,
  )?.name
  if (containerName) {
    chips.push({
      label: containerName,
      color: "primary",
      clear: () => navigate({ container_id: null }),
    })
  }
  if (props.attention_filter) {
    chips.push({
      label: "Needs attention",
      color: "primary",
      clear: () => navigate({ attention: null }),
    })
  }
  return chips
})

const sortOptions = [
  { label: "Recent", value: "recent" },
  { label: "A\u2013Z", value: "alpha" },
  { label: "Updated", value: "updated" },
]
</script>

<template>
  <Head :title="title" />

  <!-- 1. Search -->
  <SearchInput v-model="search" placeholder="Search items..." />

  <div class="bg-elevated flex w-fit rounded-md p-1" aria-label="Inventory status">
    <button
      v-for="scope in ['active', 'archived'] as const"
      :key="scope"
      :data-filter="scope"
      :aria-pressed="archive_scope === scope"
      class="rounded px-4 py-1.5 text-sm capitalize"
      :class="
        archive_scope === scope ? 'bg-default text-highlighted' : 'text-muted hover:text-default'
      "
      @click="selectArchiveScope(scope)"
    >
      {{ scope }}
    </button>
  </div>

  <!-- 2. FilterBar -->
  <FilterBar
    :tags="available_tags"
    :types="available_types"
    :containers="available_containers"
    :active-tag="tag_filter"
    :active-type-id="type_filter"
    :active-container-id="container_filter"
    :attention-active="attention_filter"
    @update:tag="(v) => navigate({ tag: v })"
    @update:type-id="(v) => navigate({ type_id: v })"
    @update:container-id="(v) => navigate({ container_id: v })"
    @update:attention="(v) => navigate({ attention: v ? '1' : null })"
  />

  <!-- 3. Active filter chips + Clear all -->
  <div v-if="activeChips.length" class="flex flex-wrap items-center gap-2">
    <UBadge
      v-for="(chip, i) in activeChips"
      :key="i"
      :color="chip.color"
      variant="subtle"
      class="gap-1"
    >
      {{ chip.label }}
      <UButton
        :icon="ICON.close"
        size="xs"
        :color="chip.color"
        variant="link"
        :padded="false"
        @click="chip.clear()"
      />
    </UBadge>
    <button class="text-primary cursor-pointer text-xs" @click="clearFilters">Clear all</button>
  </div>

  <!-- 4. Results section -->
  <div v-if="items.length" class="flex flex-col gap-2">
    <SbResultsBar>
      <template #left>
        <span class="text-muted text-xs">{{ resultCountLabel }}</span>
      </template>
      <template #right>
        <USelectMenu
          :model-value="sort"
          :items="sortOptions"
          value-key="value"
          :search-input="false"
          :ui="{ content: 'min-w-fit' }"
          class="hidden w-auto lg:flex"
          color="neutral"
          variant="ghost"
          size="xs"
          @update:model-value="(v: string) => navigate({ sort: v })"
        />
      </template>
    </SbResultsBar>

    <SbList bordered separator>
      <ListItemRow v-for="item in items" :key="item.id" :item="item" variant="index" />
    </SbList>

    <PaginationNav :pagination="pagination" />
  </div>

  <!-- Empty state -->
  <UEmpty
    v-else
    :icon="ICON.item"
    :title="
      archive_scope === 'archived'
        ? 'No archived items'
        : isFiltered
          ? 'No matching items'
          : 'No items yet'
    "
    :description="
      archive_scope === 'archived'
        ? 'Archived items will appear here.'
        : isFiltered
          ? 'Try a different search term or clear your filters.'
          : 'Start capturing to build your inventory.'
    "
    :actions="
      isFiltered || archive_scope === 'archived'
        ? []
        : [{ label: 'Start Capturing', to: capturePath() }]
    "
  />
</template>
